import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit, Trash2, Shield, ShieldOff, Copy, Check, Key,
  User as UserIcon, ChevronDown, ChevronUp, Hash, Mail, Calendar,
  BadgeCheck, XCircle, Search, Filter, X, Stethoscope, Phone
} from 'lucide-react';
import Form from '../components/Form';
import apiService from '../services/api';
import { ApiError, ApiErrorDetail, Doctor, DoctorUpdateRequest, FormField } from '../types/api';

const COPY = {
  label: 'Consultation Fee (₹)',
  currentLabel: 'Current consultation fee:',
  notSet: 'Not set',
  saveFailed: 'Failed to save doctor',
};

// The only profile fields the edit form may send. Anything else on the
// loaded doctor (password hash, email, sbd_id, blocked, timestamps) is
// read-only here and must not be echoed back (#167 Q7b).
const EDITABLE_PROFILE_FIELDS = [
  'first_name',
  'middle_name',
  'last_name',
  'date_of_birth',
  'mobile_number',
] as const;

// consultation_fee is whole INR rupees on the wire (sb-express-app spec 29),
// so the rupee value the admin types is sent as-is. Blank means "leave
// unchanged" — the field is required once a fee exists, so it can't be cleared.
const buildDoctorUpdate = (formData: Record<string, unknown>): DoctorUpdateRequest => {
  const update: DoctorUpdateRequest = {};
  for (const field of EDITABLE_PROFILE_FIELDS) {
    const value = formData[field];
    if (typeof value === 'string') {
      update[field] = value;
    }
  }
  const fee = formData.consultation_fee;
  if (typeof fee === 'number' || (typeof fee === 'string' && fee.trim() !== '')) {
    update.consultation_fee = Number(fee);
  }
  return update;
};

const consultationFeeField = (required: boolean): FormField => ({
  name: 'consultation_fee',
  label: COPY.label,
  type: 'number',
  required,
  // Whole rupees only, minimum 1 — mirrors the backend's Zod validator.
  validation: { pattern: /^\d+$/, min: 1 },
});

const formatFee = (fee?: number) =>
  typeof fee === 'number' ? `₹${fee.toLocaleString('en-IN')}` : COPY.notSet;

interface DisplayedError {
  message: string;
  details: ApiErrorDetail[];
}

// A 400's message and details are specific enough to show verbatim; other
// failures keep the page's existing message-or-fallback behaviour.
const describeSaveError = (error: unknown): DisplayedError => {
  const { status, message, details }: Partial<ApiError> =
    typeof error === 'object' && error !== null ? (error as Partial<ApiError>) : {};
  if (status === 400 && typeof message === 'string' && message) {
    return { message, details: details ?? [] };
  }
  return { message: (typeof message === 'string' && message) || COPY.saveFailed, details: [] };
};

interface DoctorCardProps {
  doctor: Doctor;
  isExpanded: boolean;
  onToggle: () => void;
  onEdit: (doctor: Doctor) => void;
  onBlock: (doctor: Doctor) => void;
  onDelete: (doctor: Doctor) => void;
  onResetPassword: (doctor: Doctor) => void;
  resetPasswordLoading: string | null;
}

const DoctorCard: React.FC<DoctorCardProps> = ({ 
  doctor, isExpanded, onToggle, onEdit, onBlock, onDelete, onResetPassword, resetPasswordLoading
}) => {
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getInitials = (doctor: Doctor) => {
    return `${doctor.first_name?.charAt(0) || ''}${doctor.last_name?.charAt(0) || ''}`.toUpperCase();
  };

  const getAvatarColor = (name: string) => {
    const colors = [
      'from-teal-500 to-emerald-600',
      'from-cyan-500 to-blue-600',
      'from-indigo-500 to-purple-600',
      'from-rose-500 to-pink-600',
      'from-amber-500 to-orange-600',
      'from-green-500 to-teal-600',
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const isBlocked = doctor.blocked === 'true';

  return (
    <div className="user-card">
      {/* Card Header - Always Visible */}
      <div className="user-card-header" onClick={onToggle}>
        <div className="user-card-left">
          <div className={`user-avatar-large bg-gradient-to-br ${getAvatarColor(doctor.first_name)}`}>
            {getInitials(doctor)}
          </div>
          <div className="user-card-info">
            <div className="user-card-name">
              Dr. {doctor.first_name} {doctor.middle_name} {doctor.last_name}
            </div>
            <div className="user-card-meta">
              <span className="user-card-id">
                <Hash size={12} />
                {doctor.sbd_id}
              </span>
              <span className="user-card-email">
                <Mail size={12} />
                {doctor.email}
              </span>
            </div>
          </div>
        </div>
        
        <div className="user-card-right">
          <div className="user-card-badges">
            <span className="user-badge badge-doctor">
              <Stethoscope size={12} /> Doctor
            </span>
            <span className={`user-badge ${isBlocked ? 'badge-blocked' : 'badge-active'}`}>
              {isBlocked ? 'Blocked' : 'Active'}
            </span>
          </div>
          <button className="expand-btn" aria-label="Toggle details">
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
        </div>
      </div>

      {/* Expanded Content */}
      <div className={`user-card-body ${isExpanded ? 'expanded' : ''}`}>
        <div className="user-details-grid">
          {/* Personal Information */}
          <div className="detail-section">
            <h4 className="detail-section-title">
              <UserIcon size={16} />
              Personal Information
            </h4>
            <div className="detail-list">
              <div className="detail-item">
                <span className="detail-label">Full Name</span>
                <span className="detail-value">
                  Dr. {doctor.first_name} {doctor.middle_name} {doctor.last_name}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Date of Birth</span>
                <span className="detail-value">
                  <Calendar size={14} className="inline mr-1" />
                  {formatDate(doctor.date_of_birth)}
                </span>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="detail-section">
            <h4 className="detail-section-title">
              <Mail size={16} />
              Contact Information
            </h4>
            <div className="detail-list">
              <div className="detail-item">
                <span className="detail-label">Email</span>
                <span className="detail-value">
                  <Mail size={14} className="inline mr-1 text-blue-500" />
                  {doctor.email}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Mobile Number</span>
                <span className="detail-value">
                  <Phone size={14} className="inline mr-1 text-green-500" />
                  {doctor.mobile_number || 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* Account Information */}
          <div className="detail-section">
            <h4 className="detail-section-title">
              <Hash size={16} />
              Account Information
            </h4>
            <div className="detail-list">
              <div className="detail-item">
                <span className="detail-label">SBD ID</span>
                <span className="detail-value font-mono">{doctor.sbd_id}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Account Status</span>
                <span className={`detail-value ${isBlocked ? 'text-red-600' : 'text-green-600'}`}>
                  {isBlocked ? 'Blocked' : 'Active'}
                </span>
              </div>
            </div>
          </div>

          {/* Timestamps */}
          <div className="detail-section">
            <h4 className="detail-section-title">
              <Calendar size={16} />
              Timeline
            </h4>
            <div className="detail-list">
              <div className="detail-item">
                <span className="detail-label">Created At</span>
                <span className="detail-value">{formatDate(doctor.createdAt)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Last Updated</span>
                <span className="detail-value">{formatDate(doctor.updatedAt)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="user-card-actions">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(doctor); }}
            className="action-btn action-edit"
            title="Edit Doctor"
          >
            <Edit size={16} />
            Edit
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onResetPassword(doctor); }}
            className="action-btn action-warning"
            title="Reset Password"
            disabled={resetPasswordLoading === doctor.sbd_id}
          >
            {resetPasswordLoading === doctor.sbd_id ? (
              <div className="spinner-small"></div>
            ) : (
              <Key size={16} />
            )}
            Reset Password
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onBlock(doctor); }}
            className={`action-btn ${isBlocked ? 'action-unblock' : 'action-block'}`}
            title={isBlocked ? 'Unblock Doctor' : 'Block Doctor'}
          >
            {isBlocked ? <Shield size={16} /> : <ShieldOff size={16} />}
            {isBlocked ? 'Unblock' : 'Block'}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(doctor); }}
            className="action-btn action-delete"
            title="Delete Doctor"
          >
            <Trash2 size={16} />
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const Doctors: React.FC = () => {
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState<Doctor | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<DisplayedError | null>(null);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [resetPasswordLoading, setResetPasswordLoading] = useState<string | null>(null);
  const [resetPasswordResult, setResetPasswordResult] = useState<{ doctorId: string; password: string } | null>(null);
  const [expandedDoctors, setExpandedDoctors] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    fetchDoctors();
  }, []);

  useEffect(() => {
    if (!editingDoctor) {
      setGeneratedPassword(null);
      setError(null);
    }
  }, [editingDoctor]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const fetchDoctors = async () => {
    try {
      setLoading(true);
      const data = await apiService.getAllDoctors();
      setDoctors(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch doctors');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (doctorId: string) => {
    setExpandedDoctors(prev => {
      const newSet = new Set(prev);
      if (newSet.has(doctorId)) {
        newSet.delete(doctorId);
      } else {
        newSet.add(doctorId);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    setExpandedDoctors(new Set(filteredDoctors.map(d => d.sbd_id)));
  };

  const collapseAll = () => {
    setExpandedDoctors(new Set());
  };

  const generatePassword = (): string => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setPasswordCopied(true);
      setTimeout(() => setPasswordCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy password:', err);
    }
  };

  const handleResetPassword = async (doctor: Doctor) => {
    if (window.confirm('Are you sure you want to reset the password for this doctor?')) {
      try {
        setResetPasswordLoading(doctor.sbd_id);
        const result = await apiService.resetDoctorPassword(doctor.sbd_id);
        setResetPasswordResult({ doctorId: doctor.sbd_id, password: result.newPassword });
        setSuccess('Password reset successfully!');
        
        setTimeout(() => {
          setResetPasswordResult(null);
        }, 10000);
        
        await fetchDoctors();
      } catch (err: any) {
        setError(err.message || 'Failed to reset password');
      } finally {
        setResetPasswordLoading(null);
      }
    }
  };

  const handleEditDoctor = (doctor: Doctor) => {
    setEditingDoctor(doctor);
    setShowForm(true);
    setError(null);
    setFormError(null);
  };

  const handleBlockDoctor = async (doctor: Doctor) => {
    try {
      await apiService.blockDoctor(doctor.sbd_id);
      const isBlocked = doctor.blocked === 'true';
      setSuccess(`Dr. ${doctor.first_name} ${isBlocked ? 'unblocked' : 'blocked'} successfully`);
      await fetchDoctors();
    } catch (err: any) {
      setError(err.message || 'Failed to block doctor');
    }
  };

  const handleDeleteDoctor = async (doctor: Doctor) => {
    if (window.confirm(`Are you sure you want to delete Dr. ${doctor.first_name} ${doctor.last_name}?`)) {
      try {
        await apiService.deleteDoctor(doctor.sbd_id);
        setSuccess(`Dr. ${doctor.first_name} deleted successfully`);
        await fetchDoctors();
      } catch (err: any) {
        setError(err.message || 'Failed to delete doctor');
      }
    }
  };

  const handleFormSubmit = async (formData: Record<string, any>) => {
    try {
      setFormLoading(true);
      setGeneratedPassword(null);
      setFormError(null);
      
      if (editingDoctor) {
        await apiService.updateDoctor(editingDoctor.sbd_id, buildDoctorUpdate(formData));
        setSuccess(`Dr. ${editingDoctor.first_name} ${editingDoctor.last_name} updated successfully!`);
        setShowForm(false);
        setEditingDoctor(null);
      } else {
        const password = generatePassword();
        const doctorData = { ...formData, password } as Doctor;
        
        await apiService.createDoctor(doctorData);
        setGeneratedPassword(password);
        setSuccess('Doctor created successfully!');
        
        setTimeout(() => {
          setShowForm(false);
          setGeneratedPassword(null);
        }, 10000);
      }
      
      await fetchDoctors();
    } catch (err: any) {
      if (editingDoctor) {
        // Shown inside the modal so the admin can correct the value in place.
        setFormError(describeSaveError(err));
      } else {
        setError(err.message || COPY.saveFailed);
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingDoctor(null);
    setGeneratedPassword(null);
    setError(null);
    setFormError(null);
  };

  const doctorFormFields: FormField[] = [
    {
      name: 'first_name',
      label: 'First Name',
      type: 'text',
      required: true,
      validation: { minLength: 2, maxLength: 50 }
    },
    {
      name: 'middle_name',
      label: 'Middle Name',
      type: 'text',
      validation: { maxLength: 50 }
    },
    {
      name: 'last_name',
      label: 'Last Name',
      type: 'text',
      required: true,
      validation: { minLength: 2, maxLength: 50 }
    },
    {
      name: 'email',
      label: 'Email',
      type: 'email',
      required: true,
      validation: { 
        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/ 
      }
    },
    {
      name: 'mobile_number',
      label: 'Mobile Number',
      type: 'text',
      validation: { maxLength: 15 }
    },
    {
      name: 'date_of_birth',
      label: 'Date of Birth',
      type: 'date',
      required: true
    }
  ];

  // Filter doctors
  const filteredDoctors = doctors.filter(doctor => {
    const matchesSearch = searchQuery === '' || 
      `${doctor.first_name} ${doctor.middle_name} ${doctor.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doctor.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doctor.sbd_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (doctor.mobile_number && doctor.mobile_number.includes(searchQuery));
    
    const isBlocked = doctor.blocked === 'true';
    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'active' && !isBlocked) ||
      (filterStatus === 'blocked' && isBlocked);

    return matchesSearch && matchesStatus;
  });

  return (
    <div className="users-page">
      {/* Header */}
      <div className="users-header">
        <div>
          <h2 className="users-title">Doctors Management</h2>
          <p className="users-subtitle">Manage all registered doctors • {doctors.length} total doctors</p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn btn-primary">
          <Plus size={16} />
          Add New Doctor
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="message message-error">
          <XCircle size={18} />
          {error}
          <button onClick={() => setError(null)} className="message-close">
            <X size={16} />
          </button>
        </div>
      )}

      {success && (
        <div className="message message-success">
          <BadgeCheck size={18} />
          {success}
          <button onClick={() => setSuccess(null)} className="message-close">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Password Reset Result */}
      {resetPasswordResult && (
        <div className="password-result-card">
          <div className="password-result-header">
            <Key size={20} />
            <span>Password Reset Successful!</span>
          </div>
          <p className="password-result-text">
            A new password has been generated. Please share this with the doctor:
          </p>
          <div className="password-display">
            <code>{resetPasswordResult.password}</code>
            <button
              onClick={() => copyToClipboard(resetPasswordResult.password)}
              className="copy-btn"
              title="Copy password"
            >
              {passwordCopied ? <Check size={16} /> : <Copy size={16} />}
            </button>
          </div>
          <p className="password-result-note">This message will disappear in 10 seconds.</p>
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={handleCancelForm}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editingDoctor ? <Edit size={20} /> : <Plus size={20} />}
                {editingDoctor ? `Edit Doctor: Dr. ${editingDoctor.first_name}` : 'Add New Doctor'}
              </h3>
              <button onClick={handleCancelForm} className="modal-close">
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {generatedPassword && (
                <div className="password-result-card" style={{ marginBottom: '1rem' }}>
                  <div className="password-result-header">
                    <BadgeCheck size={20} />
                    <span>Doctor Created Successfully!</span>
                  </div>
                  <p className="password-result-text">
                    Share this password with the doctor:
                  </p>
                  <div className="password-display">
                    <code>{generatedPassword}</code>
                    <button
                      onClick={() => copyToClipboard(generatedPassword)}
                      className="copy-btn"
                    >
                      {passwordCopied ? <Check size={16} /> : <Copy size={16} />}
                    </button>
                  </div>
                </div>
              )}
              
              {formError && (
                <div className="message message-error" role="alert">
                  <XCircle size={18} />
                  <div>
                    <p>{formError.message}</p>
                    {formError.details.length > 0 && (
                      <ul>
                        {formError.details.map((detail) => (
                          <li key={`${detail.path}:${detail.message}`}>{detail.message}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}

              {editingDoctor && (
                <p className="detail-item" data-testid="current-consultation-fee">
                  <span className="detail-label">{COPY.currentLabel}</span>{' '}
                  <span className="detail-value">{formatFee(editingDoctor.consultation_fee)}</span>
                </p>
              )}

              <Form
                fields={
                  editingDoctor
                    ? [...doctorFormFields, consultationFeeField(typeof editingDoctor.consultation_fee === 'number')]
                    : doctorFormFields
                }
                onSubmit={handleFormSubmit}
                loading={formLoading}
                initialData={editingDoctor || {}}
                submitText={editingDoctor ? 'Update Doctor' : 'Create Doctor'}
              />
            </div>
          </div>
        </div>
      )}

      {/* Filters & Search */}
      <div className="users-toolbar">
        <div className="search-box">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Search by name, email, or SBD ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="search-clear">
              <X size={16} />
            </button>
          )}
        </div>

        <div className="filters-group">
          <div className="filter-item">
            <Filter size={16} />
            <select 
              value={filterStatus} 
              onChange={(e) => setFilterStatus(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>

          <div className="expand-controls">
            <button onClick={expandAll} className="expand-btn-small" title="Expand All">
              <ChevronDown size={16} /> Expand All
            </button>
            <button onClick={collapseAll} className="expand-btn-small" title="Collapse All">
              <ChevronUp size={16} /> Collapse All
            </button>
          </div>
        </div>
      </div>

      {/* Results count */}
      <div className="results-count">
        Showing {filteredDoctors.length} of {doctors.length} doctors
      </div>

      {/* Doctor Cards */}
      <div className="users-list">
        {loading ? (
          <div className="loading-state">
            <div className="spinner-large"></div>
            <p>Loading doctors...</p>
          </div>
        ) : filteredDoctors.length === 0 ? (
          <div className="empty-state">
            <Stethoscope size={48} className="text-gray-300" />
            <h3>No doctors found</h3>
            <p>Try adjusting your search or filters</p>
          </div>
        ) : (
          filteredDoctors.map(doctor => (
            <DoctorCard
              key={doctor.sbd_id}
              doctor={doctor}
              isExpanded={expandedDoctors.has(doctor.sbd_id)}
              onToggle={() => toggleExpanded(doctor.sbd_id)}
              onEdit={handleEditDoctor}
              onBlock={handleBlockDoctor}
              onDelete={handleDeleteDoctor}
              onResetPassword={handleResetPassword}
              resetPasswordLoading={resetPasswordLoading}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default Doctors;
